import React, { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  LayoutGrid,
  AlertTriangle,
  FileText,
  SlidersHorizontal,
  Users,
  Settings,
  Lock,
  Workflow,
  ShieldAlert,
} from 'lucide-react';
import HeaderBar from '../../components/Header/HeaderBar';
import helxonLogo from '../../Images/helxonLogo.svg';
import helxonLogoLight from '../../Images/helxonLogolighttheme.svg';
import { useTheme } from '../../context/ThemeContext';
import { usePermissions, SCREENS } from '../../hooks/usePermissions';
import './layout.scss';

// Map routes to screen IDs for permission checking
const ROUTE_TO_SCREEN = {
  '/': SCREENS.DASHBOARD,
  '/incidents': SCREENS.INCIDENTS,
  '/reports': SCREENS.REPORTS,
  '/configuration': SCREENS.CONFIGURATION,
  '/customer-requests': SCREENS.CUSTOMER_REQUESTS,
  '/system-admin': SCREENS.SYSTEM_ADMIN,
  '/playbook-automation': SCREENS.PLAYBOOK_AUTOMATION,
  '/detection-rules': SCREENS.DETECTION_RULES,
};

// Get screen ID from current path
function getScreenIdFromPath(pathname) {
  // Check exact matches first
  if (ROUTE_TO_SCREEN[pathname]) {
    return ROUTE_TO_SCREEN[pathname];
  }

  // Check if path starts with a known route
  for (const [route, screenId] of Object.entries(ROUTE_TO_SCREEN)) {
    if (route !== '/' && pathname.startsWith(route)) {
      return screenId;
    }
  }

  // Default to dashboard for root
  if (pathname === '/') {
    return SCREENS.DASHBOARD;
  }

  return null;
}

// Screens not yet included in the permissions API — always allow access
const PERMISSION_EXEMPT_SCREENS = [SCREENS.PLAYBOOK_AUTOMATION, SCREENS.DETECTION_RULES, null];

function SidebarLink({ to, icon: Icon, label, screenId, beta }) {
  const { canRead, isInitialized } = usePermissions();

  // Show all links while loading, hide if no access after loaded
  // Exception: screens not yet returned by the permissions API are always shown
  if (isInitialized && !PERMISSION_EXEMPT_SCREENS.includes(screenId) && !canRead(screenId)) {
    return null;
  }

  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `side-link${beta ? ' side-link--beta' : ''} ${isActive ? 'active' : ''}`
      }
    >
      {beta && <span className="side-link__beta-text">BETA</span>}
      <div className="side-link__icon-wrap">
        <Icon size={0} />
        {beta && <span className="side-link__beta-dot" aria-hidden="true" />}
      </div>
      <span className="side-link__tooltip">
        {label}
        {beta && <em className="side-link__beta-label">Beta</em>}
      </span>
    </NavLink>
  );
}

// Route guard component
function ProtectedRoute({ children }) {
  const location = useLocation();
  const { canRead, isInitialized, isLoading } = usePermissions();

  const screenId = getScreenIdFromPath(location.pathname);

  // Show loading while permissions are being fetched
  if (!isInitialized || isLoading) {
    return (
      <div className="permission-loading">
        <div className="permission-loading__spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  // Check if user has access to this screen
  // Exception: screens not yet returned by the permissions API are always allowed
  if (screenId && !PERMISSION_EXEMPT_SCREENS.includes(screenId) && !canRead(screenId)) {
    return <AccessDenied />;
  }

  return children;
}

// Access denied component
function AccessDenied() {
  const { getReadableScreens } = usePermissions();
  const readableScreens = getReadableScreens();

  // Find the first accessible screen to redirect to
  const firstAccessibleRoute = Object.entries(ROUTE_TO_SCREEN).find(
    ([, id]) => readableScreens.includes(id)
  );

  return (
    <div className="access-denied">
      <div className="access-denied__content">
        <div className="access-denied__icon">
          <Lock size={48} />
        </div>
        <h2>Access Denied</h2>
        <p>You don't have permission to access this page.</p>
        <p className="access-denied__hint">
          Contact your administrator if you need access to this resource.
        </p>
        {firstAccessibleRoute && (
          <NavLink to={firstAccessibleRoute[0]} className="btn btn--primary">
            Go to {firstAccessibleRoute[0] === '/' ? 'Dashboard' : firstAccessibleRoute[0].replace('/', '')}
          </NavLink>
        )}
      </div>
    </div>
  );
}

export default function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { theme } = useTheme();
  const toggleSidebar = () => setSidebarOpen((s) => !s);

  const handleContentClick = () => {
    if (sidebarOpen) setSidebarOpen(false);
  };

  return (
    <div className={`app-shell ${sidebarOpen ? 'is-open' : ''}`}>
      <div className="app-background" />

      <HeaderBar onToggleSidebar={toggleSidebar} isSidebarOpen={sidebarOpen} />

      <aside className={`sidebar ${sidebarOpen ? 'sidebar--open' : ''}`}>
        <div className="sidebar-shell">
          <div className="sidebar-content">
            <nav className="nav">
              <SidebarLink
                to="/"
                icon={LayoutGrid}
                label="Dashboard"
                screenId={SCREENS.DASHBOARD}
              />
              <SidebarLink
                to="/incidents"
                icon={AlertTriangle}
                label="Incidents"
                screenId={SCREENS.INCIDENTS}
              />
              <SidebarLink
                to="/reports"
                icon={FileText}
                label="Reports"
                screenId={SCREENS.REPORTS}
              />
              <SidebarLink
                to="/configuration"
                icon={SlidersHorizontal}
                label="Configuration"
                screenId={SCREENS.CONFIGURATION}
              />
              <SidebarLink
                to="/customer-requests"
                icon={Users}
                label="Customer Requests"
                screenId={SCREENS.CUSTOMER_REQUESTS}
              />
              <SidebarLink
                to="/detection-rules"
                icon={ShieldAlert}
                label="Detection Rules"
                screenId={SCREENS.DETECTION_RULES}
              />
              {/* <SidebarLink
                to="/playbook-automation"
                icon={Workflow}
                label="Playbook Automation"
                screenId={SCREENS.PLAYBOOK_AUTOMATION}
                beta
              /> */}
              <SidebarLink
                to="/system-admin"
                icon={Settings}
                label="System Admin"
                screenId={SCREENS.SYSTEM_ADMIN}
              />
            </nav>
          </div>
        </div>

        <div className="sidebar__footer">
          <div className="powered-by">
            <span>Powered by</span>
            <img
              src={theme === 'dark' ? helxonLogo : helxonLogoLight}
              alt="Helxon"
              className="helxon-logo"
            />
          </div>
        </div>
      </aside>

      <main className="content" onClick={handleContentClick}>
        <ProtectedRoute>
          <Outlet />
        </ProtectedRoute>
      </main>
    </div>
  );
}
